/**
 * PhonesSection Component
 *
 * Displays and manages phone numbers for a contact or company.
 * Supports add/delete operations with inline forms.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi, companiesApi } from '@/services/contactsApi';
import type { ContactPhone, CompanyPhone, ContactPhoneInput, CompanyPhoneInput } from '../types';

interface PhonesSectionProps {
  phones: (ContactPhone | CompanyPhone)[];
  entityId: string;
  entityType: 'contact' | 'company';
  onUpdate: () => void;
  onError: (error: string) => void;
}

export default function PhonesSection({
  phones,
  entityId,
  entityType,
  onUpdate,
  onError,
}: PhonesSectionProps) {
  const { t } = useTranslation('people');
  const [showAdd, setShowAdd] = useState(false);
  const [newPhone, setNewPhone] = useState<ContactPhoneInput | CompanyPhoneInput>({
    phone: '',
    phone_type: entityType === 'contact' ? 'mobile' : 'main',
    is_primary: false,
  });

  const handleAdd = async () => {
    if (!newPhone.phone.trim()) return;

    try {
      if (entityType === 'contact') {
        await contactsApi.addPhone(entityId, newPhone as ContactPhoneInput);
      } else {
        await companiesApi.addPhone(entityId, newPhone as CompanyPhoneInput);
      }
      onUpdate();
      setShowAdd(false);
      setNewPhone({
        phone: '',
        phone_type: entityType === 'contact' ? 'mobile' : 'main',
        is_primary: false,
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add phone');
    }
  };

  const handleDelete = async (phoneId: string, phoneNumber: string) => {
    if (!confirm(`Delete phone ${phoneNumber}?`)) return;

    try {
      if (entityType === 'contact') {
        await contactsApi.deletePhone(entityId, phoneId);
      } else {
        await companiesApi.deletePhone(entityId, phoneId);
      }
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete phone');
    }
  };

  const phoneTypes = entityType === 'contact'
    ? ['work', 'mobile', 'home', 'other']
    : ['main', 'sales', 'support', 'fax'];

  return (
    <div className="mb-6">
      <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('sections.phones.label')}</label>
      <div className="space-y-2">
        {phones && phones.length > 0 ? (
          phones.map((phone) => (
            <div key={phone.id} className="flex items-center gap-2 text-sm group">
              <button
                onClick={() => handleDelete(phone.id, phone.phone)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                title={t('sections.phones.delete_phone')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <a href={`tel:${phone.phone}`} className="text-blue-600 hover:underline">
                {phone.phone}
              </a>
              <span className="text-xs text-gray-400">({phone.phone_type})</span>
              {phone.is_primary && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">primary</span>
              )}
            </div>
          ))
        ) : (
          <span className="text-gray-400 text-sm">{t('sections.phones.no_phones')}</span>
        )}

        {/* Add Phone Form */}
        {showAdd ? (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
            <input
              type="tel"
              value={newPhone.phone}
              onChange={(e) => setNewPhone({ ...newPhone, phone: e.target.value })}
              placeholder="+1 555-123-4567"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newPhone.phone.trim()) {
                  e.preventDefault();
                  handleAdd();
                }
                if (e.key === 'Escape') {
                  setShowAdd(false);
                  setNewPhone({ phone: '', phone_type: entityType === 'contact' ? 'mobile' : 'main', is_primary: false });
                }
              }}
            />
            <div className="flex gap-2">
              <select
                value={newPhone.phone_type}
                onChange={(e) => setNewPhone({ ...newPhone, phone_type: e.target.value })}
                className="px-2 py-1 text-sm border border-gray-300 rounded"
              >
                {phoneTypes.map((type) => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={newPhone.is_primary}
                  onChange={(e) => setNewPhone({ ...newPhone, is_primary: e.target.checked })}
                />
                {t('sections.phones.primary')}
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newPhone.phone.trim()}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('sections.phones.add')}
              </button>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setNewPhone({ phone: '', phone_type: entityType === 'contact' ? 'mobile' : 'main', is_primary: false });
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('sections.phones.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="mt-1 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('sections.phones.add_phone')}
          </button>
        )}
      </div>
    </div>
  );
}
